import { ProductPaymentDialog } from "@/components/catalog/ProductPaymentDialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import {
  fetchInvestorActivity,
  investorActivityErrorMessage,
  payInvestorReservedCash,
  type InvestorActivity,
  type InvestorActivityAllocation,
  type InvestorOpenDistribution,
} from "@/lib/investors/investorActivityStore";
import {
  investorSettlementErrorMessage,
  recordInvestorSettlement,
} from "@/lib/catalog/investorSettlementStore";
import { productPaymentErrorMessage } from "@/lib/catalog/productPaymentsStore";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import { cn } from "@/lib/utils";
import {
  ArrowDownLeft,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  CreditCard,
  FileText,
  HandCoins,
  Loader2,
  RefreshCw,
  X,
} from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  investorId: string | null;
  investorName: string;
  canPay: boolean;
  onSettled?: () => void;
};

function formatDate(value: string | null): string {
  if (!value) return "—";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

function moneyPlain(amount: number, currency?: string): string {
  return formatCurrency(Math.abs(amount), currency);
}

function ProductNameLink({
  productId,
  children,
  className,
}: {
  productId: number | null | undefined;
  children: ReactNode;
  className?: string;
}) {
  if (!productId) {
    return <span className={cn("text-xs font-semibold text-foreground leading-snug", className)}>{children}</span>;
  }
  return (
    <a
      href={`/products/${productId}`}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "text-xs font-semibold text-primary hover:underline underline-offset-2 leading-snug",
        className,
      )}
    >
      {children}
    </a>
  );
}

function productDisplayName(opts: {
  product_id?: number | null;
  product_name?: string | null;
  description?: string | null;
  fallback?: string;
}): string {
  const name = (opts.product_name || "").trim();
  if (name) return name;
  const desc = (opts.description || "").trim();
  if (desc) return desc;
  if (opts.product_id) return `Vehicle #${opts.product_id}`;
  return opts.fallback || "—";
}

function whatHappened(row: InvestorActivityAllocation): string {
  switch (row.entry_type) {
    case "reinvest_out":
      return "Sale profit received";
    case "reinvest_in":
      if (!row.product_id && !row.destination_product_id) {
        return "Reserved for next vehicle (TBD)";
      }
      return "Reinvested into vehicle";
    case "cash_pay":
      return "Cash / bank payout";
    default:
      return "Investment movement";
  }
}

/** TBD / unassigned reserve rows are not timeline minus lines — shown under closing balance. */
function isTbdReserve(row: InvestorActivityAllocation): boolean {
  return row.entry_type === "reinvest_in" && (row.product_id == null || row.product_id <= 0);
}

/**
 * Cash-pool style sign:
 * + money came back from a sold vehicle
 * − money put into a named vehicle / paid out in cash
 * (TBD reserve is excluded from the timeline)
 */
function signedAmount(row: InvestorActivityAllocation): number {
  if (row.entry_type === "reinvest_out") {
    return Math.abs(row.credit > 0.009 ? row.credit : row.amount);
  }
  if (row.entry_type === "reinvest_in") {
    return -Math.abs(row.debit > 0.009 ? row.debit : row.amount);
  }
  if (row.entry_type === "cash_pay") {
    return -Math.abs(row.credit > 0.009 ? row.credit : row.amount);
  }
  const raw = row.debit > 0.009 ? row.debit : -row.credit;
  return Math.round(raw * 100) / 100;
}

type TimelineLine = {
  id: number;
  date: string | null;
  what: string;
  entryType: string;
  saleId: number | null;
  sourceProductId: number | null;
  sourceProductName: string | null;
  destinationProductId: number | null;
  destinationProductName: string | null;
  destinationDescription: string | null;
  signed: number;
  balance: number;
};

function buildTimeline(allocations: InvestorActivityAllocation[]): TimelineLine[] {
  const sorted = [...allocations]
    .filter((row) => !isTbdReserve(row))
    .sort((a, b) => {
      const da = a.entry_date || "";
      const db = b.entry_date || "";
      if (da !== db) return da.localeCompare(db);
      return a.id - b.id;
    });
  let running = 0;
  return sorted.map((row) => {
    const signed = signedAmount(row);

    // Reinvest float only holds money that came in via reinvest_out (+).
    // Direct cash payout from open payable never sat in that float — show the
    // payout line, but do not leave a phantom negative balance (abs-displayed as
    // if money were still in the account).
    if (row.entry_type === "cash_pay" && signed < -0.009) {
      const availableFloat = Math.max(0, running);
      const reduceBy = Math.min(Math.abs(signed), availableFloat);
      running = Math.round((running - reduceBy) * 100) / 100;
    } else {
      running = Math.round((running + signed) * 100) / 100;
      if (running < 0 && Math.abs(running) < 0.01) running = 0;
    }

    const sourceProductId = row.source_product_id ?? (row.entry_type === "reinvest_out" ? row.product_id : null);
    const sourceProductName =
      row.source_product_name ??
      (row.entry_type === "reinvest_out" ? row.product_name : null) ??
      null;

    let destinationProductId = row.destination_product_id ?? null;
    let destinationProductName = row.destination_product_name ?? null;
    let destinationDescription = row.destination_description ?? null;

    if (row.entry_type === "reinvest_in") {
      destinationProductId = row.destination_product_id ?? row.product_id;
      destinationProductName = row.destination_product_name ?? row.product_name ?? null;
      destinationDescription = row.destination_description ?? row.description ?? null;
    } else if (row.entry_type === "cash_pay") {
      destinationProductName = "Cash / bank";
      destinationDescription = null;
      destinationProductId = null;
    }

    return {
      id: row.id,
      date: row.entry_date,
      what: whatHappened(row),
      entryType: row.entry_type,
      saleId: row.sale_id ?? null,
      sourceProductId,
      sourceProductName,
      destinationProductId,
      destinationProductName,
      destinationDescription,
      signed,
      balance: Math.max(0, running),
    };
  });
}

function SummaryCard({
  title,
  amount,
  hint,
  tone = "neutral",
  icon,
  currency,
  action,
}: {
  title: string;
  amount: number;
  hint: string;
  tone?: "neutral" | "amber" | "emerald" | "rose";
  icon: ReactNode;
  currency?: string;
  action?: ReactNode;
}) {
  const amountClass =
    tone === "amber"
      ? "text-amber-700 dark:text-amber-400"
      : tone === "emerald"
        ? "text-emerald-700 dark:text-emerald-400"
        : tone === "rose"
          ? "text-rose-700 dark:text-rose-400"
          : "text-foreground";

  const iconWrap =
    tone === "amber"
      ? "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"
      : tone === "emerald"
        ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
        : tone === "rose"
          ? "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20"
          : "bg-muted/60 text-muted-foreground border-border/60";

  return (
    <div className="rounded-2xl border border-border/70 bg-card/60 p-4 shadow-xs space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{title}</p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">{hint}</p>
        </div>
        <div className={cn("h-7 w-7 rounded-lg flex items-center justify-center shrink-0 border", iconWrap)}>
          {icon}
        </div>
      </div>
      <p className={cn("text-xl sm:text-2xl font-extrabold tracking-tight tabular-nums", amountClass)}>
        {moneyPlain(amount, currency)}
      </p>
      {action}
    </div>
  );
}

export function InvestorActivityDialog({
  open,
  onOpenChange,
  investorId,
  investorName,
  canPay,
  onSettled,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [activity, setActivity] = useState<InvestorActivity | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payDistribution, setPayDistribution] = useState<InvestorOpenDistribution | null>(null);
  const [reservedPayOpen, setReservedPayOpen] = useState(false);

  const load = useCallback(async () => {
    if (!investorId) return;
    setLoading(true);
    setError(null);
    try {
      setActivity(await fetchInvestorActivity(investorId));
    } catch (e) {
      setActivity(null);
      setError(investorActivityErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [investorId]);

  useEffect(() => {
    if (!open || !investorId) {
      setActivity(null);
      setError(null);
      setPayOpen(false);
      setPayDistribution(null);
      setReservedPayOpen(false);
      return;
    }
    void load();
  }, [open, investorId, load]);

  const openPay = (pd: InvestorOpenDistribution) => {
    if (!pd.product_id) {
      toast.error("Sold vehicle was not found for this payment.");
      return;
    }
    setPayDistribution(pd);
    setPayOpen(true);
  };

  const openReservedPay = () => {
    const sourceProductId = activity?.reserved_tbd_source_product_id;
    if (!sourceProductId) {
      toast.error("Source sale vehicle was not found for reserved amount.");
      return;
    }
    setReservedPayOpen(true);
  };

  const handleReservedCashSubmit = async (payload: {
    amount: number;
    payment_date: string;
    payment_method: string | null;
    bank_id: number | null;
    bank_name: string | null;
    transaction_reference_no: string | null;
    pay_order_no: string | null;
    cheque_no?: string | null;
    deposit_slip_no?: string | null;
    notes: string | null;
  }) => {
    if (!investorId) {
      throw new Error("Investor was not found.");
    }
    try {
      await payInvestorReservedCash(investorId, {
        amount: payload.amount,
        payment_date: payload.payment_date,
        payment_method: payload.payment_method,
        bank_id: payload.bank_id,
        bank_name: payload.bank_name,
        transaction_reference_no: payload.transaction_reference_no,
        pay_order_no: payload.pay_order_no,
        cheque_no: payload.cheque_no ?? null,
        deposit_slip_no: payload.deposit_slip_no ?? null,
        notes: payload.notes,
      });
      toast.success("Reserved amount paid out in cash. Investment ledger updated.");
      setReservedPayOpen(false);
      await load();
      onSettled?.();
    } catch (e) {
      toast.error(investorActivityErrorMessage(e) || productPaymentErrorMessage(e));
      throw e;
    }
  };

  const handlePaymentSubmit = async (payload: {
    amount: number;
    payment_date: string;
    payment_method: string | null;
    funding_source?: "cash_bank" | "investor_balance";
    bank_id: number | null;
    bank_name: string | null;
    transaction_reference_no: string | null;
    pay_order_no: string | null;
    cheque_no?: string | null;
    deposit_slip_no?: string | null;
    notes: string | null;
    action: "pay" | "receive";
    party_id: number;
    transaction_type?: string | null;
    scheduled_payment_id?: number | null;
    product_invoice_id?: number | null;
    currency_code: string | null;
    currency_amount: number | null;
    exchange_rate: number | null;
    settlement_mode?: "cash_pay" | "reinvest";
    profit_distribution_id?: number | null;
    destination_product_id?: number | null;
    destination_description?: string | null;
  }) => {
    if (!payDistribution?.product_id) {
      throw new Error("Sold vehicle was not found for this payment.");
    }
    const productId = payDistribution.product_id;
    const profitDistributionId = Number(
      payload.profit_distribution_id ?? payDistribution.id,
    );
    try {
      if (!profitDistributionId) {
        throw new Error("Please choose a payable share before reinvesting.");
      }
      // Open payable is reinvest-only. Cash comes later from Reserved (TBD).
      await recordInvestorSettlement(productId, {
        profit_distribution_id: profitDistributionId,
        refund_amount: 0,
        reinvest_amount: payload.amount,
        destination_description: payload.destination_description?.trim() || null,
        destination_product_id: null,
        allocated_at: payload.payment_date,
        notes: payload.notes,
      });
      toast.success("Reinvestment saved. Use Pay cash on Reserved when the investor wants cash.");
      setPayOpen(false);
      setPayDistribution(null);
      await load();
      onSettled?.();
    } catch (e) {
      toast.error(investorSettlementErrorMessage(e) || productPaymentErrorMessage(e));
      throw e;
    }
  };

  const currency = activity?.currency;
  const cars = activity?.reinvest_cars ?? [];
  const openDistributions = activity?.open_distributions ?? [];
  const hasOpenPayable = openDistributions.some((pd) => pd.available > 0.009 || pd.balance > 0.009);

  const timeline = useMemo(
    () => buildTimeline(activity?.allocations ?? []),
    [activity?.allocations],
  );

  const reservedTbd = useMemo(() => {
    const fromCars =
      Math.round(
        cars.filter((c) => c.is_tbd).reduce((sum, c) => sum + Math.max(0, c.net_amount), 0) * 100,
      ) / 100;
    return fromCars > 0.005 ? fromCars : Math.round(Number(activity?.totals.reserved_tbd ?? 0) * 100) / 100;
  }, [cars, activity?.totals.reserved_tbd]);

  const closingBalance = timeline.length ? timeline[timeline.length - 1].balance : 0;
  const accountBalance = Math.round(closingBalance * 100) / 100;
  const freeBalance = Math.round(Math.max(0, accountBalance - reservedTbd) * 100) / 100;
  const reinvestedTotal = Math.round(Number(activity?.totals.reinvested ?? 0) * 100) / 100;
  const cashPaidTotal = Math.round(Number(activity?.totals.cash_paid ?? 0) * 100) / 100;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          hideCloseButton
          className="flex h-[92vh] max-h-[92vh] w-[calc(100vw-2rem)] max-w-6xl flex-col overflow-hidden p-0 gap-0 bg-background border border-border/80 rounded-2xl shadow-2xl"
        >
          <DialogHeader className="shrink-0 px-6 sm:px-7 pt-5 pb-4 border-b border-border/50 bg-background/95 backdrop-blur-xs z-10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-xl border border-primary/20 bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5" />
                </div>
                <div className="space-y-0.5 min-w-0">
                  <DialogTitle className="text-lg sm:text-xl font-bold font-display tracking-tight text-foreground">
                    Investor Investment Ledger
                    {investorName ? ` — ${investorName}` : ""}
                  </DialogTitle>
                  <p className="text-xs text-muted-foreground">
                    Sale profit received, reinvested vehicles, cash payouts, and closing balance
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs font-semibold gap-1.5"
                  onClick={() => void load()}
                  disabled={loading}
                >
                  {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  Refresh
                </Button>
                <DialogClose asChild>
                  <Button type="button" variant="ghost" size="icon" className="h-8 w-8 rounded-lg">
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                  </Button>
                </DialogClose>
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 sm:px-7 py-5 space-y-5">
            {loading ? (
              <div className="py-20 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading investment ledger…
              </div>
            ) : error ? (
              <div className="py-16 space-y-3 text-center">
                <p className="text-sm text-destructive">{error}</p>
                <Button type="button" variant="glass" size="sm" onClick={() => void load()}>
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                  Try again
                </Button>
              </div>
            ) : activity ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                  <SummaryCard
                    title="Account balance"
                    amount={accountBalance}
                    hint="Still in investor account"
                    currency={currency}
                    icon={<HandCoins className="h-4 w-4" />}
                  />
                  <SummaryCard
                    title="Reserved (TBD)"
                    amount={reservedTbd}
                    hint="Held for next vehicle"
                    tone="amber"
                    currency={currency}
                    icon={<ArrowUpRight className="h-4 w-4" />}
                    action={
                      canPay && reservedTbd > 0.009 ? (
                        <Button
                          type="button"
                          size="sm"
                          className="h-8 w-full text-xs gap-1.5"
                          onClick={openReservedPay}
                          disabled={!activity?.reserved_tbd_source_product_id}
                        >
                          <CreditCard className="h-3.5 w-3.5" />
                          Pay cash
                        </Button>
                      ) : null
                    }
                  />
                  <SummaryCard
                    title="Free balance"
                    amount={freeBalance}
                    hint="After reserved amount"
                    tone="emerald"
                    currency={currency}
                    icon={<ArrowDownLeft className="h-4 w-4" />}
                  />
                  <SummaryCard
                    title="Cash paid out"
                    amount={cashPaidTotal}
                    hint={`Reinvested ${moneyPlain(reinvestedTotal, currency)}`}
                    tone="rose"
                    currency={currency}
                    icon={<CreditCard className="h-4 w-4" />}
                  />
                </div>

                <div className="space-y-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="font-bold text-base text-foreground tracking-tight">Amounts still to pay</h2>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground border border-border/40">
                        {openDistributions.length} open
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Outstanding profit shares — reinvest first; cash payout is available from Reserved after that
                    </p>
                  </div>

                  {!hasOpenPayable ? (
                    <div className="flex items-center gap-2 rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 text-sm text-emerald-700 dark:text-emerald-400">
                      <CheckCircle2 className="h-4 w-4 shrink-0" />
                      All clear — nothing left to pay
                    </div>
                  ) : (
                    <div className="rounded-xl border border-border/70 bg-card/30 overflow-hidden shadow-xs">
                      <div className="overflow-x-auto max-h-52">
                        <Table className="w-full min-w-[40rem] border-collapse">
                          <TableHeader>
                            <TableRow className="hover:bg-transparent border-b border-border/60 bg-muted/30">
                              <TableHead className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground p-2 h-auto">
                                Sale vehicle
                              </TableHead>
                              <TableHead className="text-right text-[10px] font-bold uppercase tracking-wider text-muted-foreground p-2 h-auto">
                                Total share
                              </TableHead>
                              <TableHead className="text-right text-[10px] font-bold uppercase tracking-wider text-muted-foreground p-2 h-auto">
                                Paid
                              </TableHead>
                              <TableHead className="text-right text-[10px] font-bold uppercase tracking-wider text-muted-foreground p-2 h-auto">
                                Remaining
                              </TableHead>
                              {canPay ? (
                                <TableHead className="text-right text-[10px] font-bold uppercase tracking-wider text-muted-foreground p-2 h-auto w-[1%]">
                                  Action
                                </TableHead>
                              ) : null}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {openDistributions.map((pd) => (
                              <TableRow key={pd.id} className="border-b border-border/40 hover:bg-muted/15">
                                <TableCell className="p-2 align-middle">
                                  <ProductNameLink productId={pd.product_id}>
                                    {pd.product_name ||
                                      (pd.product_id ? `Vehicle #${pd.product_id}` : "Sold vehicle")}
                                  </ProductNameLink>
                                </TableCell>
                                <TableCell className="p-2 text-right tabular-nums text-xs font-semibold">
                                  {moneyPlain(pd.final_payable, currency)}
                                </TableCell>
                                <TableCell className="p-2 text-right tabular-nums text-xs">
                                  {moneyPlain(pd.paid_amount, currency)}
                                </TableCell>
                                <TableCell className="p-2 text-right tabular-nums text-xs font-bold">
                                  {moneyPlain(pd.balance, currency)}
                                </TableCell>
                                {canPay ? (
                                  <TableCell className="p-2 text-right">
                                    {pd.available > 0.009 && pd.product_id ? (
                                      <Button
                                        size="sm"
                                        className="h-7 px-2.5 text-xs gap-1"
                                        onClick={() => openPay(pd)}
                                      >
                                        <RefreshCw className="h-3 w-3" />
                                        Reinvest
                                      </Button>
                                    ) : null}
                                  </TableCell>
                                ) : null}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-3 pt-1">
                  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="font-bold text-base text-foreground tracking-tight">Investment ledger</h2>
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground border border-border/40">
                          {timeline.length} records
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Shows which sold product the money came from, and whether it was invested into a vehicle or paid out in cash
                      </p>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 font-semibold">
                        <ArrowDownLeft className="h-3 w-3" />
                        Received
                      </span>
                      {" · "}
                      <span className="inline-flex items-center gap-1 text-rose-700 dark:text-rose-400 font-semibold">
                        <ArrowUpRight className="h-3 w-3" />
                        Invested / paid
                      </span>
                    </p>
                  </div>

                  {timeline.length === 0 ? (
                    <div className="rounded-xl border border-border/40 bg-muted/10 px-4 py-6 text-sm text-muted-foreground text-center">
                      No payout or reinvestment activity yet.
                    </div>
                  ) : (
                    <div className="rounded-xl border border-border/70 bg-card/30 overflow-hidden shadow-xs">
                      <div className="overflow-x-auto">
                        <Table className="w-full min-w-[48rem] border-collapse">
                          <TableHeader>
                            <TableRow className="hover:bg-transparent border-b border-border/60 bg-muted/30">
                              <TableHead className="w-[12%] whitespace-nowrap text-[10px] font-bold uppercase tracking-wider text-muted-foreground p-2 h-auto">
                                Date
                              </TableHead>
                              <TableHead className="w-[18%] whitespace-nowrap text-[10px] font-bold uppercase tracking-wider text-muted-foreground p-2 h-auto">
                                Particulars
                              </TableHead>
                              <TableHead className="w-[42%] whitespace-nowrap text-[10px] font-bold uppercase tracking-wider text-muted-foreground p-2 h-auto">
                                From sale → Invested into / Cash
                              </TableHead>
                              <TableHead className="w-[14%] whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-muted-foreground p-2 h-auto">
                                Amount
                              </TableHead>
                              <TableHead className="w-[14%] whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-muted-foreground p-2 h-auto pr-3">
                                Balance
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {timeline.map((line) => {
                              const isPlus = line.signed >= 0;
                              const sourceLabel = productDisplayName({
                                product_id: line.sourceProductId,
                                product_name: line.sourceProductName,
                                fallback: "—",
                              });
                              let destinationLabel = "—";
                              let destinationProductId: number | null = null;
                              if (line.entryType === "cash_pay") {
                                destinationLabel = "Cash / bank";
                              } else if (
                                line.destinationProductId ||
                                line.destinationProductName ||
                                line.destinationDescription
                              ) {
                                destinationProductId = line.destinationProductId;
                                destinationLabel = productDisplayName({
                                  product_id: line.destinationProductId,
                                  product_name: line.destinationProductName,
                                  description: line.destinationDescription,
                                  fallback: "—",
                                });
                              } else if (line.entryType === "reinvest_out") {
                                destinationLabel = "Account";
                              }

                              return (
                                <TableRow
                                  key={line.id}
                                  className="border-b border-border/40 hover:bg-muted/15 transition-colors"
                                >
                                  <TableCell className="align-middle p-2">
                                    <p className="text-xs font-semibold text-foreground whitespace-nowrap">
                                      {formatDate(line.date)}
                                    </p>
                                  </TableCell>

                                  <TableCell className="align-middle p-2 min-w-0">
                                    <p className="text-xs font-bold text-foreground">{line.what}</p>
                                  </TableCell>

                                  <TableCell className="align-middle p-2 min-w-0">
                                    <div className="flex items-center gap-2 min-w-0">
                                      {sourceLabel !== "—" ? (
                                        <ProductNameLink productId={line.sourceProductId} className="truncate">
                                          {sourceLabel}
                                        </ProductNameLink>
                                      ) : (
                                        <span className="text-xs text-muted-foreground">—</span>
                                      )}
                                      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                      {destinationProductId ||
                                      (destinationLabel !== "—" &&
                                        destinationLabel !== "Cash / bank" &&
                                        destinationLabel !== "Account") ? (
                                        <ProductNameLink productId={destinationProductId} className="truncate">
                                          {destinationLabel}
                                        </ProductNameLink>
                                      ) : (
                                        <span className="text-xs font-semibold text-foreground truncate">
                                          {destinationLabel}
                                        </span>
                                      )}
                                    </div>
                                  </TableCell>

                                  <TableCell className="align-middle p-2 text-right">
                                    <span
                                      className={cn(
                                        "inline-flex items-center gap-1 text-xs font-bold tabular-nums whitespace-nowrap",
                                        isPlus
                                          ? "text-emerald-700 dark:text-emerald-400"
                                          : "text-rose-700 dark:text-rose-400",
                                      )}
                                    >
                                      {isPlus ? (
                                        <ArrowDownLeft className="h-3 w-3" />
                                      ) : (
                                        <ArrowUpRight className="h-3 w-3" />
                                      )}
                                      {isPlus ? "+" : "−"}
                                      {moneyPlain(line.signed, currency)}
                                    </span>
                                  </TableCell>

                                  <TableCell className="align-middle p-2 pr-3 text-right tabular-nums text-xs font-semibold text-foreground">
                                    {moneyPlain(line.balance, currency)}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                          <TableFooter>
                            <TableRow className="bg-muted/20 hover:bg-muted/20">
                              <TableCell colSpan={4} className="p-2 text-xs font-bold text-foreground">
                                Closing balance
                              </TableCell>
                              <TableCell className="p-2 pr-3 text-right tabular-nums text-xs font-bold">
                                {moneyPlain(accountBalance, currency)}
                              </TableCell>
                            </TableRow>
                            <TableRow className="bg-muted/10 hover:bg-muted/10">
                              <TableCell colSpan={4} className="p-2 text-xs text-muted-foreground font-medium">
                                Reserved for next vehicle (TBD)
                              </TableCell>
                              <TableCell className="p-2 pr-3 text-right tabular-nums text-xs font-semibold text-amber-700 dark:text-amber-400">
                                {moneyPlain(reservedTbd, currency)}
                              </TableCell>
                            </TableRow>
                            <TableRow className="bg-muted/10 hover:bg-muted/10">
                              <TableCell colSpan={4} className="p-2 text-xs text-muted-foreground font-medium">
                                Free balance (after reserved)
                              </TableCell>
                              <TableCell className="p-2 pr-3 text-right tabular-nums text-xs font-bold text-emerald-700 dark:text-emerald-400">
                                {moneyPlain(freeBalance, currency)}
                              </TableCell>
                            </TableRow>
                          </TableFooter>
                        </Table>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {payDistribution?.product_id && investorId ? (
        <ProductPaymentDialog
          open={payOpen}
          onOpenChange={(o) => {
            setPayOpen(o);
            if (!o) setPayDistribution(null);
          }}
          productId={payDistribution.product_id}
          outstanding={payDistribution.available > 0 ? payDistribution.available : payDistribution.balance}
          action="pay"
          partyName={investorName || "Investor / Owner"}
          partyId={Number(investorId)}
          transactionType="deal_partner"
          scheduledPaymentId={0}
          productInvoiceId={null}
          allowExcess={false}
          profitDistributionId={payDistribution.id}
          allowCashFromPayable={false}
          onSubmit={handlePaymentSubmit}
        />
      ) : null}

      {activity?.reserved_tbd_source_product_id && investorId && reservedTbd > 0.009 ? (
        <ProductPaymentDialog
          open={reservedPayOpen}
          onOpenChange={setReservedPayOpen}
          productId={activity.reserved_tbd_source_product_id}
          outstanding={reservedTbd}
          action="pay"
          partyName={investorName || "Investor / Owner"}
          partyId={Number(investorId)}
          transactionType="deal_partner"
          scheduledPaymentId={0}
          productInvoiceId={null}
          allowExcess={false}
          profitDistributionId={null}
          onSubmit={async (payload) => {
            await handleReservedCashSubmit(payload);
          }}
        />
      ) : null}
    </>
  );
}
